import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types'; // Good practice for prop validation
import { Box, Typography, CircularProgress, Alert, Paper, Container, Grid } from '@mui/material';
import moment from 'moment'; // Used for date formatting, install if needed: npm install moment or yarn add moment
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/constants';
import { transformToBTC } from '../../utils/helpers';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import { fetchWalletDetailsAPI } from '../TradingTools/WalletCard.component';

// --- Configuration ---
// Adjust this to match your frontend's API base URL

const CurrentBTCBalance = ({ userId }) => {
    const { price: btcPrice } = useBitcoinPrice(); // <<< Get BTC price from context
    const { t } = useTranslation();

    let [balanceData, setBalanceData] = useState(null);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) {
            setError(t('user.userIdNotProvided'));
            setLoading(false);
            setBalanceData(null); // Clear any old data if userId becomes null
            return; // Don't fetch if no userId
        }

        const fetchBalance = async () => {
            setLoading(true);
            setError(null); // Clear previous errors on new fetch attempt
            setBalanceData(null); // Clear previous data

            try {
                // Construct the API endpoint URL using the userId prop
                const response = await fetch(`https://api.odin.fun/v1/user/${userId}/stats`)


                const data = await response.json();
                setBalanceData(data);

            } catch (err) {
                console.error("Fetch BTC balance error:", err);
                setError(err.message || t('user.failedToFetchBalance'));
            } finally {
                setLoading(false);
            }
        };

        fetchBalance();


    }, [userId]); // Dependency array: This effect re-runs whenever the userId prop changes

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{t('user.loadingBTCBalance')}</Typography>
            </Box>
        );
    }
    if (error) {
        return <Alert severity="error">{t('user.errorLoadingBalance', { error })}</Alert>;
    }
    const balanceInUSD = balanceData ? balanceData.data.btc / 1e11 * btcPrice : 0; // Convert BTC to USD using the price from context

    return (<Grid item xs={12} sm={4}>
        <Typography variant="body2" color="grey.500">{t('user.currentBalance')}</Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {transformToBTC(balanceData.data.btc)}{' '}
            <Typography component="span" variant="caption" color="grey.500">
                ({balanceInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
            </Typography>
        </Typography>
    </Grid>);

};
// --- React Component ---
const UserBtcBalance = ({ userId }) => {
    const { price: btcPrice } = useBitcoinPrice(); // <<< Get BTC price from context
    const { t } = useTranslation();

    const [balanceData, setBalanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) {
            setError('User ID is not provided.');
            setLoading(false);
            setBalanceData(null); // Clear any old data if userId becomes null
            return; // Don't fetch if no userId
        }

        const fetchBalance = async () => {
            setLoading(true);
            setError(null); // Clear previous errors on new fetch attempt
            setBalanceData(null); // Clear previous data

            try {
                // Construct the API endpoint URL using the userId prop
                const response = await fetch(`${ API_BASE_URL }/users/${ userId }/btc-balance`); // <-- Adjust this URL if necessary

                if (!response.ok) {
                    // Attempt to parse error message from response body
                    const errorBody = await response.json().catch(() => ({ error: 'Unknown server error' }));
                    throw new Error(errorBody.error || `HTTP error! status: ${ response.status }`);
                }

                const data = await response.json();
                setBalanceData(data);

            } catch (err) {
                console.error("Fetch BTC balance error:", err);
                setError(err.message || 'Failed to fetch balance data.');
            } finally {
                setLoading(false);
            }
        };

        fetchBalance();

    }, [userId]); // Dependency array: This effect re-runs whenever the userId prop changes

    // --- Rendering Logic ---

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading BTC Balance...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Error loading balance: {error}</Alert>;
    }

    const balanceInUSD = balanceData ? balanceData.balance / 1e11 * btcPrice : 0; // Convert BTC to USD using the price from context
    const depositsInUSD = balanceData ? balanceData.totalDeposits / 1e11 * btcPrice : 0; // Convert deposits to USD
    const withdrawalsInUSD = balanceData ? balanceData.totalWithdrawals / 1e11 * btcPrice : 0; // Convert withdrawals to USD

    return (<Box sx={{ mb: 4 }}>
        {/* <Typography variant="h6" sx={{ mb: 2 }}>Balance</Typography> */}
        <Grid container spacing={2}>
            <CurrentBTCBalance userId={userId} />

            {/* <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="grey.500">Current Balance</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {transformToBTC(balanceData.balance)}{' '}
                    <Typography component="span" variant="caption" color="grey.500">
                        ({balanceInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
                    </Typography>
                </Typography>
            </Grid>  */}
            <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="grey.500">{t('user.totalDeposits')}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {transformToBTC(balanceData.totalDeposits)}{' '}
                    <Typography component="span" variant="caption" color="grey.500">
                        ({depositsInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
                    </Typography>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="grey.500">{t('user.totalWithdrawals')}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {transformToBTC(balanceData.totalWithdrawals)}{' '}
                    <Typography component="span" variant="caption" color="grey.500">
                        ({withdrawalsInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
                    </Typography>
                </Typography>
            </Grid>
        </Grid>
    </Box>);

    // If data is loaded and no error, display the balance details
    return (
        <Container maxWidth="md" variant="outlined" sx={{ p: 2, mb: 3, color: "grey.900", borderRadius: 2, border: "1px solid rgba(255,255,255,.255)" }}> {/* Slightly different background */}
            {
                balanceData && (
                    <Typography variant="caption" display="block" color="grey.500" sx={{ mb: 1.5, textAlign: 'right' }}>
                        Last Updated: {balanceData.lastUpdated ? moment(balanceData.lastUpdated).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                    </Typography>
                )
            }
            <Typography variant="h6" gutterBottom align="center">
                BTC Balance
            </Typography>
            {/* Optional: Display User ID if helpful */}
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                User ID: {userId}
            </Typography>

            {balanceData ? (
                <Box sx={{ '& > :not(style)': { mb: 1.5 } }}> {/* Add some spacing between lines using Box */}
                    <Typography variant="body1">
                        Current Balance: <Typography component="span" fontWeight="bold">
                            {transformToBTC(balanceData.balance)} <span style={{ fontSize: '0.8em' }}>({balanceInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})</span>
                        </Typography>
                    </Typography>
                    <Typography variant="body1">
                        Total Deposits: <Typography component="span" fontWeight="bold" color="success.main"> {/* Optional color for deposits */}
                            {transformToBTC(balanceData.totalDeposits)} <span style={{ fontSize: '0.8em' }}>({depositsInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})</span>
                        </Typography>
                    </Typography>
                    <Typography variant="body1">
                        Total Withdrawals: <Typography component="span" fontWeight="bold" color="error.main"> {/* Optional color for withdrawals */}
                            {transformToBTC(balanceData.totalWithdrawals)} <span style={{ fontSize: '0.8em' }}>({withdrawalsInUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})</span>
                        </Typography>
                    </Typography>
                </Box>
            ) : (
                // This case should ideally be covered by loading/error, but is a good fallback
                <Typography variant="body1" color="text.secondary">No balance data found for this user.</Typography>
            )}
        </Container>
    );
};

// --- PropTypes for type checking and documentation ---
UserBtcBalance.propTypes = {
    userId: PropTypes.string.isRequired, // userId is expected to be a required string
};

export default UserBtcBalance;